import * as layout from '../../../components/markLayout.module.css';
import { MarkButton, MarkInput, MarkLabel } from '../../../components/MarkControls';
import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { flushSync } from 'react-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import BackButton from '../../../../../components/BackButton';
import InlineLoader from '../../../../../components/InlineLoader';
import QueryInlineError from '../../../../../components/QueryInlineError';
import ConfirmationModal from '../../../../../components/ConfirmationModal';
import useDrip from '../../../api/useDrip';
import useUpdateDrip from '../../../api/useUpdateDrip';
import useValidateDrip from '../../../api/useValidateDrip';
import useBack from '../../../../../hooks/useBack';
import useDripDraft from '../../../hooks/useDripDraft';
import useDripGraphValidation from '../../../hooks/useDripGraphValidation';
import useUnsavedChangesRegistration from '../../../hooks/useUnsavedChangesRegistration';
import { UnsavedChangesContext } from '../../../components/UnsavedChangesProvider';
import DripStatusPill from '../DripStatusPill';
import ValidateBanner from './ValidateBanner';
import VersionSwitch from './VersionSwitch';
import Overview from './Overview';
import OverviewEdit from './OverviewEdit';
import Steps from './Steps';
import StepCanvasEditor from '../StepCanvasEditor';
import MarkModal from '../../../components/MarkModal';
import Versions from './Versions';
import Stats from './Stats';
import Test from './Test';
import ActionsBar from './ActionsBar';
import ActivateModal from './ActivateModal';
import PauseModal from './PauseModal';
import DraftBanner from './DraftBanner';
import { detailBasePath, isAtPath, isTestTabPath, testTabPath } from './demoTestLogic';
import { extractDripErrorMessage } from '../dripFormUtils';
import type { DripValidationDestination } from '../../../constants';
import { SEND_WINDOW_TIME_ZONE_HINT } from '../../../constants';
import { useNotifications } from '../../../../../context/notification';
import type { AxiosError } from 'axios';
import type { DemoDripInstance, UpdateDripBody, UpdateDripResponse } from '../../../types';
type DetailTab = 'overview' | 'steps' | 'versions' | 'stats' | 'test';
type VersionSide = 'draft' | 'current';
const DripDetail: React.FC = () => {
    const { dripId } = useParams<{
        dripId: string;
    }>();
    const location = useLocation();
    const navigate = useNavigate();
    const back = useBack();
    const tabId = useId();
    const requestedTab = new URLSearchParams(location.search).get('tab');
    const nonTestTab: DetailTab =
        requestedTab === 'steps' || requestedTab === 'versions' || requestedTab === 'stats'
            ? requestedTab
            : 'overview';
    const activeTab: DetailTab = isTestTabPath(location.pathname) ? 'test' : nonTestTab;
    const numericId = Number(dripId);
    const { data: dripResponse, isLoading, error, refetch: refetchDrip } = useDrip(numericId);
    const drip = dripResponse?.data;
    const [selectedSide, setSelectedSide] = useState<VersionSide | null>(null);
    // Owned here, not by Test, so switching tabs doesn't drop the list.
    const [demoInstances, setDemoInstances] = useState<DemoDripInstance[]>([]);
    const prevDripId = useRef(dripId);
    if (prevDripId.current !== dripId) {
        prevDripId.current = dripId;
        setSelectedSide(null);
        setDemoInstances([]);
    }
    const displayVersion =
        selectedSide === 'draft'
            ? (drip?.draft_version ?? drip?.current_version ?? null)
            : selectedSide === 'current'
              ? (drip?.current_version ?? drip?.draft_version ?? null)
              : (drip?.draft_version ?? drip?.current_version ?? null);
    const tabs: {
        key: DetailTab;
        label: string;
    }[] = [
        { key: 'overview', label: 'Overview' },
        { key: 'steps', label: 'Steps' },
        { key: 'versions', label: 'Versions' },
        { key: 'stats', label: 'Stats' },
        { key: 'test', label: 'Test' }
    ];
    // Keep the detail history entry on Steps when its template opens on another page.
    const goToTab = (tab: DetailTab) => {
        const search = new URLSearchParams(location.search);
        if (tab !== 'test') search.set('tab', tab);
        const targetPath =
            tab === 'test' ? testTabPath(dripId ?? '') : detailBasePath(dripId ?? '');
        const targetSearch = search.toString();
        if (
            !isAtPath(location.pathname, targetPath) ||
            location.search !== (targetSearch ? `?${targetSearch}` : '')
        ) {
            navigate(
                { pathname: targetPath, search: targetSearch },
                // Keep the list's return URL for "Back to drips".
                { replace: tab !== 'test' && activeTab !== 'test', state: location.state }
            );
        }
    };
    const activateTab = (index: number) => {
        const tab = tabs[index];
        goToTab(tab.key);
        requestAnimationFrame(() => document.getElementById(`${tabId}-tab-${tab.key}`)?.focus());
    };
    const navigateValidation = (destination: DripValidationDestination) => {
        if (isSaving) return;
        if (destination === 'list') navigate('/mark/drips');
        else activateTab(tabs.findIndex((tab) => tab.key === destination));
    };
    const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        let nextIndex: number | null = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        activateTab(nextIndex);
    };
    const draft = useDripDraft(drip);
    const validation = useDripGraphValidation(
        draft.state.steps,
        draft.state.entryStepId,
        draft.state.sendWindowStart,
        draft.state.sendWindowEnd
    );
    const sendWindowErrors = validation.errors.filter((error) => error.source === 'send_window');
    const updateDrip = useUpdateDrip();
    const [preSaveChecking, setPreSaveChecking] = useState(false);
    const isSaving = updateDrip.isPending || preSaveChecking;
    const { addNotification } = useNotifications();
    const notifyStructuralSave = (response: UpdateDripResponse) => {
        addNotification({
            type: 'success',
            message: response.new_version_created
                ? `Draft version v${response.draft_version?.version_number} created.`
                : `Draft v${response.draft_version?.version_number ?? response.current_version?.version_number} saved.`
        });
    };
    const isDraft = drip?.status === 'DRAFT';
    const isForkable = drip?.status === 'ACTIVE' || drip?.status === 'PAUSED';
    const hasDraftVersion = drip?.draft_version != null;
    const viewingDraft = selectedSide !== 'current';
    // Backend updates preserve an existing draft, including its templates.
    const canEditSteps = viewingDraft && (isDraft || isForkable);
    const unregisterSteps = useUnsavedChangesRegistration('drip-steps', draft.dirty, isSaving);

    const validateQuery = useValidateDrip(drip?.drip_id ?? 0);
    const [stepsEditorOpen, setStepsEditorOpen] = useState(false);
    const [lifecycleModal, setLifecycleModal] = useState<'activate' | 'pause' | null>(null);
    const [overviewEditField, setOverviewEditField] = useState<'audience' | 'schedule' | null>(
        null
    );
    // A structural save against a drip whose status or draft moved since the
    // edits started could fork a second draft (and drop its templates) or
    // overwrite someone else's — re-check against the server first.
    const saveStructural = async (onSaved?: (saved: UpdateDripResponse) => void) => {
        if (!drip || validation.errors.length > 0 || isSaving) return;
        const startedFrom = draft.base;
        const body: UpdateDripBody = draft.buildUpdateBody(drip.drip_id);
        setPreSaveChecking(true);
        const latest = await refetchDrip().finally(() => setPreSaveChecking(false));
        const fresh = latest.data?.data;
        if (latest.isError || !fresh) {
            addNotification({
                type: 'error',
                message: 'Could not re-check the drip before saving. Try again.'
            });
            return;
        }
        if (
            !startedFrom ||
            fresh.status !== startedFrom.status ||
            (fresh.draft_version?.version_number ?? null) !== startedFrom.draftVersionNumber ||
            // DRAFT saves update in place under the same version number.
            !draft.matchesPersisted(fresh)
        ) {
            draft.stageLatest(fresh);
            addNotification({
                type: 'error',
                message: `Not saved — this drip changed since you started editing (now ${fresh.status}${fresh.draft_version ? `, draft v${fresh.draft_version.version_number}` : ', no draft'}). Discard your edits to load the latest version.`
            });
            return;
        }
        updateDrip.mutate(body, {
            onSuccess: (response) => {
                const newVersion = response.data.draft_version ?? response.data.current_version;
                if (newVersion) {
                    flushSync(() => {
                        draft.markClean(newVersion);
                        unregisterSteps();
                    });
                }
                notifyStructuralSave(response.data);
                onSaved?.(response.data);
            }
        });
    };
    const handleSave = () => {
        void saveStructural();
    };
    const { registerDiscardHandler } = useContext(UnsavedChangesContext);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    // Lifecycle actions must use the persisted graph, so confirm structural edits first.
    const structuralDirty = draft.dirty;

    // DripDetail stays mounted across a Test-tab switch (only its URL
    // changes), so a discard confirmed via the app-wide blocker — entering
    // Test — must actually clear this page's own dirty state, not just let
    // the navigation through. The blocker has no notion of draft.discard();
    // this is how it finds out. Registered once; reads current dirty state
    // via refs so the handler is never stale without needing to re-register
    // on every render.
    const structuralDirtyRef = useRef(structuralDirty);
    structuralDirtyRef.current = structuralDirty;
    useEffect(
        () =>
            registerDiscardHandler(() => {
                if (structuralDirtyRef.current) draft.discard();
            }),
        [registerDiscardHandler, draft.discard]
    );
    const guardedLifecycleRun = useCallback(
        (action: () => void) => {
            if (isSaving) return;
            if (structuralDirty) {
                setPendingAction(() => action);
            } else {
                action();
            }
        },
        [isSaving, structuralDirty]
    );
    const handleDiscardPending = () => {
        const action = pendingAction;
        setPendingAction(null);
        flushSync(() => {
            draft.discard();
            unregisterSteps();
        });
        action?.();
    };
    const handleCancelPending = () => {
        setPendingAction(null);
    };
    const openTemplate = (id: number, versionNumber: number, stepId: string) => {
        const search = new URLSearchParams(location.search);
        search.set('tab', 'steps');
        navigate(
            {
                pathname: `/mark/drips/${id}/versions/${versionNumber}/steps/${encodeURIComponent(stepId)}/template`,
                search: search.toString()
            },
            { state: location.state }
        );
    };
    const handleEditTemplate = (stepId: string) => {
        if (!drip?.draft_version || !canEditSteps || isSaving) return;
        const saved = draft.persistedSteps.get(stepId);
        const local = draft.state.steps.find((s) => s.id === stepId);
        if (!saved || saved.type !== 'activity' || !local || local.type !== 'activity') return;
        // Unsaved channel change: the template must be opened via Save & edit.
        if (local.activity !== saved.activity) return;
        if (saved.activity !== 'send_email' && saved.activity !== 'send_sms') return;
        openTemplate(drip.drip_id, drip.draft_version.version_number, stepId);
    };
    const handleSaveAndEditTemplate = (stepId: string) => {
        if (!drip || !canEditSteps || validation.errors.length > 0 || isSaving) return;
        void saveStructural((saved) => {
            const version = saved.draft_version;
            const step = version?.steps.find((item) => item.id === stepId);
            if (
                !version ||
                !step ||
                step.type !== 'activity' ||
                (step.activity !== 'send_email' && step.activity !== 'send_sms')
            ) {
                addNotification({
                    type: 'error',
                    message:
                        'The saved step is unavailable for template editing. Reopen Steps and try again.'
                });
                return;
            }
            openTemplate(saved.drip_id, version.version_number, step.id);
        });
    };
    if (isLoading) return <InlineLoader />;
    // Keep cached detail and local edits mounted after a failed refetch.
    if (error && !drip) {
        return <QueryInlineError error={error} />;
    }
    if (!drip) return <P>Drip not found.</P>;
    return (
        <Section subtheme={['regular', 'background']} className={layout.page} noDefaultPadding>
            <BackButton onBack={() => back('/mark/drips')} alt="Back to drips" />
            <P
                noDefaultMargin
                size="small"
                styles={{
                    margin: '0.8rem 0',
                    color: 'var(--mark-muted)'
                }}
            >
                <Link
                    to="/mark"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Mark
                </Link>
                {' > '}
                <Link
                    to="/mark/drips"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Drips
                </Link>
                {' > '}
                {drip.name}
            </P>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '1.6rem',
                    marginBottom: '1.2rem'
                }}
            >
                <div>
                    <H role="h4" noDefaultMargin>
                        {drip.name}
                    </H>
                    <div
                        style={{
                            display: 'flex',
                            gap: '0.8rem',
                            alignItems: 'center',
                            marginTop: '0.4rem'
                        }}
                    >
                        <DripStatusPill status={drip.status} />
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Created: {new Date(drip.created).toLocaleDateString()}
                        </P>
                    </div>
                </div>
                <div className={layout.actions}>
                    <ActionsBar
                        status={drip.status}
                        hasDraftVersion={hasDraftVersion}
                        draftVersionNumber={drip.draft_version?.version_number}
                        onValidate={() =>
                            guardedLifecycleRun(() => {
                                goToTab('overview');
                                validateQuery.refetch();
                            })
                        }
                        onActivate={() => guardedLifecycleRun(() => setLifecycleModal('activate'))}
                        onPause={() => guardedLifecycleRun(() => setLifecycleModal('pause'))}
                    />
                </div>
            </div>
            <div
                role="tablist"
                aria-label="Drip details"
                style={{
                    borderBottom: '2px solid var(--mark-border)',
                    marginBottom: '1.6rem'
                }}
                className={layout.tabs}
            >
                {tabs.map((tab, index) => (
                    <MarkButton
                        key={tab.key}
                        type="button"
                        role="tab"
                        id={`${tabId}-tab-${tab.key}`}
                        aria-selected={activeTab === tab.key}
                        aria-controls={`${tabId}-panel-${tab.key}`}
                        tabIndex={activeTab === tab.key ? 0 : -1}
                        onClick={() => activateTab(index)}
                        onKeyDown={(event) => handleTabKeyDown(event, index)}
                        style={{
                            marginBottom: '-2px'
                        }}
                    >
                        {tab.label}
                    </MarkButton>
                ))}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-overview`}
                aria-labelledby={`${tabId}-tab-overview`}
                hidden={activeTab !== 'overview'}
                tabIndex={0}
            >
                {activeTab === 'overview' && (
                    <>
                        <ValidateBanner dripId={drip.drip_id} onNavigate={navigateValidation} />
                        <Overview
                            drip={drip}
                            onEditAudience={() => setOverviewEditField('audience')}
                            onEditSchedule={() => setOverviewEditField('schedule')}
                            scheduleEditDisabled={draft.dirty}
                        />
                    </>
                )}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-steps`}
                aria-labelledby={`${tabId}-tab-steps`}
                hidden={activeTab !== 'steps'}
                tabIndex={0}
            >
                {activeTab === 'steps' && (
                    <VersionSwitch
                        currentVersion={drip.current_version}
                        draftVersion={drip.draft_version}
                        activeVersionNumber={displayVersion?.version_number ?? null}
                        onSelect={setSelectedSide}
                    />
                )}
                {activeTab === 'steps' && isForkable && !hasDraftVersion && viewingDraft && (
                    <DraftBanner variant="no-draft" status={drip.status} />
                )}
                {activeTab === 'steps' && canEditSteps && draft.newerVersionNumber != null && (
                    <div
                        role="status"
                        style={{
                            marginBottom: '1rem',
                            backgroundColor: 'var(--mark-warning-surface)',
                            color: 'var(--mark-warning)'
                        }}
                        className={layout.panel}
                    >
                        <P noDefaultMargin size="small">
                            This drip was saved elsewhere (now v{draft.newerVersionNumber}) while
                            you had unsaved edits. Your edits are kept but are based on an older
                            copy — Discard to load the latest.
                        </P>
                    </div>
                )}
                {activeTab === 'steps' && canEditSteps && (
                    <>
                        {draft.dirty && (
                            <P noDefaultMargin size="small">
                                Unsaved step changes. Reopen the editor to save or discard them.
                            </P>
                        )}
                        <Steps
                            version={displayVersion}
                            onEditSteps={() => setStepsEditorOpen(true)}
                            onEditTemplate={hasDraftVersion ? handleEditTemplate : undefined}
                        />
                        {stepsEditorOpen && (
                            <MarkModal
                                aria-label="Edit drip steps"
                                onClose={() => setStepsEditorOpen(false)}
                                isPending={isSaving}
                            >
                                <H role="h4">Edit drip steps</H>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '0.8rem',
                                        alignItems: 'center',
                                        padding: '0.6rem 0',
                                        marginBottom: '1rem',
                                        borderBottom: '1px solid var(--mark-border)',
                                        backgroundColor: 'var(--mark-surface)'
                                    }}
                                >
                                    {draft.dirty && (
                                        <span
                                            style={{
                                                width: '0.6rem',
                                                height: '0.6rem',
                                                borderRadius: '50%',
                                                backgroundColor: 'var(--mark-primary)',
                                                flexShrink: 0
                                            }}
                                            aria-label="Unsaved changes"
                                        />
                                    )}
                                    <MarkButton
                                        type="button"
                                        onClick={handleSave}
                                        disabled={
                                            !draft.dirty || validation.errors.length > 0 || isSaving
                                        }
                                    >
                                        {isSaving ? 'Saving…' : 'Save'}
                                    </MarkButton>
                                    <MarkButton
                                        type="button"
                                        onClick={draft.discard}
                                        disabled={
                                            (!draft.dirty && draft.newerVersionNumber == null) ||
                                            isSaving
                                        }
                                    >
                                        Discard
                                    </MarkButton>
                                    {updateDrip.isError && (
                                        <P
                                            noDefaultMargin
                                            size="small"
                                            styles={{
                                                color: 'var(--mark-danger)'
                                            }}
                                        >
                                            {extractDripErrorMessage(
                                                updateDrip.error as AxiosError
                                            )}
                                        </P>
                                    )}
                                </div>
                                <div className={layout.fieldGrid}>
                                    <div className={layout.field}>
                                        <MarkLabel
                                            htmlFor="sw-start"
                                            style={{
                                                display: 'block',
                                                marginBottom: '0.2rem'
                                            }}
                                        >
                                            Send window start
                                        </MarkLabel>
                                        <MarkInput
                                            id="sw-start"
                                            type="time"
                                            value={draft.state.sendWindowStart ?? ''}
                                            onChange={(e) =>
                                                draft.setSendWindow(
                                                    e.currentTarget.value || null,
                                                    draft.state.sendWindowEnd
                                                )
                                            }
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={layout.field}>
                                        <MarkLabel
                                            htmlFor="sw-end"
                                            style={{
                                                display: 'block',
                                                marginBottom: '0.2rem'
                                            }}
                                        >
                                            Send window end
                                        </MarkLabel>
                                        <MarkInput
                                            id="sw-end"
                                            type="time"
                                            value={draft.state.sendWindowEnd ?? ''}
                                            onChange={(e) =>
                                                draft.setSendWindow(
                                                    draft.state.sendWindowStart,
                                                    e.currentTarget.value || null
                                                )
                                            }
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                                {sendWindowErrors.length > 0 && (
                                    <P
                                        noDefaultMargin
                                        size="small"
                                        styles={{
                                            color: 'var(--mark-muted)'
                                        }}
                                    >
                                        {SEND_WINDOW_TIME_ZONE_HINT}
                                    </P>
                                )}
                                <fieldset
                                    disabled={isSaving}
                                    style={{
                                        border: 'none',
                                        padding: 0,
                                        margin: 0
                                    }}
                                >
                                    <StepCanvasEditor
                                        draft={draft}
                                        dripId={drip.drip_id}
                                        draftVersionNumber={drip.draft_version?.version_number}
                                        dripTargetKind={drip.target_kind}
                                        onEditTemplate={handleEditTemplate}
                                        onSaveAndEditTemplate={handleSaveAndEditTemplate}
                                        saveAndEditTemplateDisabled={
                                            validation.errors.length > 0 || isSaving
                                        }
                                        sendWindowErrors={sendWindowErrors}
                                    />
                                </fieldset>
                            </MarkModal>
                        )}
                    </>
                )}
                {activeTab === 'steps' && !canEditSteps && <Steps version={displayVersion} />}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-versions`}
                aria-labelledby={`${tabId}-tab-versions`}
                hidden={activeTab !== 'versions'}
                tabIndex={0}
            >
                {activeTab === 'versions' && <Versions drip={drip} />}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-stats`}
                aria-labelledby={`${tabId}-tab-stats`}
                hidden={activeTab !== 'stats'}
                tabIndex={0}
            >
                {activeTab === 'stats' && <Stats drip={drip} />}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-test`}
                aria-labelledby={`${tabId}-tab-test`}
                hidden={activeTab !== 'test'}
                tabIndex={0}
            >
                {activeTab === 'test' && (
                    <Test
                        drip={drip}
                        instances={demoInstances}
                        onInstancesChange={setDemoInstances}
                    />
                )}
            </div>
            {pendingAction && (
                <ConfirmationModal
                    title="Discard unsaved changes?"
                    description="This drip has unsaved step changes that will be lost if you continue."
                    confirmBtnLabel="Discard"
                    cancelBtnLabel="Stay"
                    onConfirm={handleDiscardPending}
                    onCancel={handleCancelPending}
                />
            )}
            {lifecycleModal === 'activate' && (
                <ActivateModal
                    drip={drip}
                    onNavigate={navigateValidation}
                    onClose={() => setLifecycleModal(null)}
                    onSuccess={() => setLifecycleModal(null)}
                />
            )}
            {lifecycleModal === 'pause' && (
                <PauseModal
                    drip={drip}
                    onClose={() => setLifecycleModal(null)}
                    onSuccess={() => setLifecycleModal(null)}
                />
            )}
            {overviewEditField && (
                <OverviewEdit
                    drip={drip}
                    field={overviewEditField}
                    onClose={() => setOverviewEditField(null)}
                    onSuccess={() => setOverviewEditField(null)}
                    onScheduleSaved={draft.markClean}
                />
            )}
        </Section>
    );
};
export default DripDetail;
